// The panel's structural primitives: a labelled fact, a titled block, and a named group of
// blocks.
//
// All three disappear when they have nothing to show. That is the panel's central rule: a
// block reading "this account has not been triaged" takes up the same room as the real thing
// while saying the opposite, so absence is rendered as absence.

import type { ReactNode } from 'react';

// Render a labelled fact only when it has a value, so thin enrichment yields a short grid
// rather than a full grid of blanks.
export function Fact({ label, value }: { label: string; value: ReactNode }) {
  if (value == null || value === '' || value === false) return null;
  return (
    <div className="fact">
      <div className="fl">{label}</div>
      <div className="fv">{value}</div>
    </div>
  );
}

export function SideBlock({
  heading,
  hint,
  children,
}: {
  heading: string;
  hint?: string | null;
  children: ReactNode;
}) {
  if (!children) return null;
  return (
    <div className="sblock">
      <div className="sblockhead">
        <div className="dh">{heading}</div>
        {hint ? <span className="hint">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

// A named group of blocks.
//
// One flat list of thirteen blocks separated by hairlines gave the panel a single level of
// hierarchy, and at that density People, Touch history and Customer journeys -- three lists of
// name-and-meta rows -- were genuinely hard to tell apart while scrolling. Grouping says what
// each stretch of the panel is *for*, and the blocks inside can then be cards without the panel
// reading as thirteen identical boxes.
//
// A group with no surviving blocks disappears with them, the same way an empty block does.
export function SideZone({ label, children }: { label: string; children: ReactNode }) {
  // `children` is an array with nulls in it wherever a block opted out. If every entry is
  // null the whole group goes, which is why this counts rather than trusting truthiness.
  const surviving = Array.isArray(children)
    ? children.flat(Infinity).filter(Boolean)
    : [children].filter(Boolean);
  if (!surviving.length) return null;

  return (
    <section className="szone">
      <div className="zonehead">
        <span className="zlab">{label}</span>
        <span className="zrule" />
      </div>
      {surviving}
    </section>
  );
}

// Values separated by a middot, used by the identity strip and every `pmeta` line.
export function Separated({ parts }: { parts: ReactNode[] }) {
  const kept = parts.filter(Boolean);
  return (
    <>
      {kept.map((part, index) => (
        // Index keys are correct here: these lists are rebuilt wholesale from one record and
        // never reordered or filtered in place.
        <span key={index}>
          {index > 0 ? <span className="sep">·</span> : null}
          {part}
        </span>
      ))}
    </>
  );
}
